// SPDX-License-Identifier: AGPL-3.0-or-later

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Archive,
  ArchiveRestore,
  ChevronRight,
  Download,
  Flag,
  Keyboard,
  MoreVertical,
  Search,
  X,
} from 'lucide-react';
import { Dialog, DropdownMenu, PageHeader, useToast } from '@intakeform/ui';
import { useQuestionnaires } from '../../api/questionnaires.js';
import {
  useResponse,
  useResponses,
  useUpdateResponseArchive,
  useUpdateResponseFlag,
  useUpdateResponseStatus,
} from '../../api/responses.js';
import type { ResponseDetail, ResponseSummary } from '../../api/types.js';

/**
 * Clinician response triage surface (Clinical Slate, Pass 5).
 *
 * A dense, keyboard-driven list with a right-hand split-panel detail.
 * Status is a dot + label + sr-only context (colour is quaternary).
 * Keyboard: j/k move · Enter open · Esc close · / search · x select ·
 * f flag · e export · ? shortcuts. Actions are wired to real endpoints
 * (status change, flag toggle); export is a client-side CSV. Notes and
 * the audit timeline live on the full record (/responses/:id), linked
 * from the panel.
 */

interface StatusMeta {
  label: string;
  dot: string;
}
const STATUS: Record<string, StatusMeta> = {
  'in-progress': { label: 'In progress', dot: '--color-neutral' },
  'needs-review': { label: 'Needs review', dot: '--color-warning' },
  'in-review': { label: 'In review', dot: '--color-primary' },
  completed: { label: 'Completed', dot: '--color-info' },
  verified: { label: 'Verified', dot: '--color-success' },
  'entered-in-error': { label: 'Error', dot: '--color-danger' },
};
const STATUS_ORDER = [
  'needs-review',
  'in-review',
  'completed',
  'verified',
  'in-progress',
  'entered-in-error',
] as const;

function statusMeta(status: string): StatusMeta {
  return STATUS[status] ?? { label: status, dot: '--color-neutral' };
}

function StatusBadge({ status }: { status: string }) {
  const meta = statusMeta(status);
  return (
    <span className="rsp-status">
      <span
        className="rsp-status-dot"
        style={{ background: `rgb(var(${meta.dot}))` }}
        aria-hidden="true"
      />
      <span>{meta.label}</span>
    </span>
  );
}

function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

interface RowVM {
  id: string;
  name: string;
  form: string;
  status: string;
  flagged: boolean;
  submittedAt: string | null;
  ref: string;
}

function patientName(r: ResponseSummary): string {
  return r.patient?.displayName ?? r.patient?.email ?? `Patient ${r.patientId.slice(0, 8)}`;
}

function csvCell(v: string): string {
  return `"${v.replace(/"/g, '""')}"`;
}

function downloadCsv(filename: string, rows: RowVM[]): void {
  const header = ['Patient', 'Form', 'Status', 'Flagged', 'Submitted', 'Reference'];
  const body = rows.map((r) => [
    r.name,
    r.form,
    statusMeta(r.status).label,
    r.flagged ? 'yes' : 'no',
    formatWhen(r.submittedAt),
    r.ref,
  ]);
  const csv = [header, ...body].map((cols) => cols.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const SHORTCUTS: [string, string][] = [
  ['j / ↓', 'Next response'],
  ['k / ↑', 'Previous response'],
  ['Enter', 'Open detail'],
  ['Esc', 'Close detail or dialog'],
  ['/', 'Focus search'],
  ['x', 'Select / deselect row'],
  ['f', 'Flag / unflag row'],
  ['e', 'Export row (CSV)'],
  ['a', 'Archive / unarchive row'],
  ['?', 'Toggle this sheet'],
];

export function ResponsesListRoute() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [viewArchived, setViewArchived] = useState(false);
  const responsesQuery = useResponses(viewArchived ? { archived: true } : {});
  const questionnairesQuery = useQuestionnaires();
  const flagMutation = useUpdateResponseFlag();
  const statusMutation = useUpdateResponseStatus();
  const archiveMutation = useUpdateResponseArchive();

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all'); // 'all' | status | 'flagged'
  const [checked, setChecked] = useState<ReadonlySet<string>>(() => new Set());
  const [cursor, setCursor] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  const titleById = useMemo(() => {
    const m = new Map<string, string>();
    for (const q of questionnairesQuery.data ?? []) m.set(q.id, q.title);
    return m;
  }, [questionnairesQuery.data]);

  const rows = useMemo<RowVM[]>(() => {
    return (responsesQuery.data ?? []).map((r) => ({
      id: r.id,
      name: patientName(r),
      form: titleById.get(r.questionnaireId) ?? 'Intake form',
      status: r.status,
      flagged: r.flagged,
      submittedAt: r.submittedAt,
      ref: r.id.slice(0, 8),
    }));
  }, [responsesQuery.data, titleById]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length, flagged: 0 };
    for (const s of STATUS_ORDER) c[s] = 0;
    for (const r of rows) {
      c[r.status] = (c[r.status] ?? 0) + 1;
      if (r.flagged) c['flagged'] = (c['flagged'] ?? 0) + 1;
    }
    return c;
  }, [rows]);

  const filtered = useMemo<RowVM[]>(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesFilter =
        statusFilter === 'all'
          ? true
          : statusFilter === 'flagged'
            ? r.flagged
            : r.status === statusFilter;
      const matchesQuery =
        q === '' ||
        r.name.toLowerCase().includes(q) ||
        r.form.toLowerCase().includes(q) ||
        r.ref.toLowerCase().includes(q);
      return matchesFilter && matchesQuery;
    });
  }, [rows, query, statusFilter]);

  // Keep the cursor in range as the filtered set changes.
  useEffect(() => {
    setCursor((c) => (c > filtered.length - 1 ? Math.max(0, filtered.length - 1) : c));
  }, [filtered.length]);

  const openRow = useCallback((id: string) => {
    lastFocused.current = document.activeElement as HTMLElement | null;
    setOpenId(id);
  }, []);

  const closeDetail = useCallback(() => {
    setOpenId(null);
    lastFocused.current?.focus?.();
  }, []);

  const toggleCheck = useCallback((id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const flagRow = useCallback(
    (row: RowVM) => {
      flagMutation.mutate(
        { id: row.id, flagged: !row.flagged },
        {
          onSuccess: () =>
            toast({
              title: row.flagged ? `Flag removed from ${row.name}` : `${row.name} flagged`,
              variant: 'default',
            }),
          onError: () => toast({ title: 'Could not update flag', variant: 'danger' }),
        },
      );
    },
    [flagMutation, toast],
  );

  const exportRows = useCallback(
    (toExport: RowVM[]) => {
      if (toExport.length === 0) return;
      downloadCsv(`responses-${toExport.length}.csv`, toExport);
      toast({
        title: `Exported ${toExport.length} response${toExport.length > 1 ? 's' : ''} to CSV`,
        variant: 'success',
      });
    },
    [toast],
  );

  const markReviewed = useCallback(
    (id: string, name: string) => {
      statusMutation.mutate(
        { id, status: 'verified' },
        {
          onSuccess: () => toast({ title: `${name} marked reviewed`, variant: 'success' }),
          onError: () => toast({ title: 'Could not update status', variant: 'danger' }),
        },
      );
    },
    [statusMutation, toast],
  );

  // Active view archives; the archived view un-archives.
  const archiveRows = useCallback(
    (toArchive: RowVM[]) => {
      if (toArchive.length === 0) return;
      const target = !viewArchived;
      for (const r of toArchive) archiveMutation.mutate({ id: r.id, archived: target });
      toast({
        title: `${toArchive.length} ${target ? 'archived' : 'unarchived'}`,
        variant: 'default',
      });
    },
    [archiveMutation, viewArchived, toast],
  );

  const bulkFlag = useCallback(() => {
    const ids = [...checked];
    for (const id of ids) flagMutation.mutate({ id, flagged: true });
    toast({ title: `${ids.length} flagged`, variant: 'default' });
  }, [checked, flagMutation, toast]);

  const bulkExport = useCallback(() => {
    exportRows(filtered.filter((r) => checked.has(r.id)));
  }, [exportRows, filtered, checked]);

  const bulkArchive = useCallback(() => {
    archiveRows(filtered.filter((r) => checked.has(r.id)));
    setChecked(new Set());
  }, [archiveRows, filtered, checked]);

  // Keyboard scheme (documented in the ? sheet).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName ?? '').toUpperCase();
      const typing = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

      if (e.key === 'Escape') {
        if (showShortcuts) return; // Radix Dialog owns its own Esc
        if (openId !== null) {
          closeDetail();
          return;
        }
        if (typing) {
          (document.activeElement as HTMLElement).blur();
          return;
        }
      }
      if (typing) return;

      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (e.key === '?') {
        e.preventDefault();
        setShowShortcuts((s) => !s);
        return;
      }
      if (showShortcuts) return;

      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(filtered.length - 1, c + 1));
        return;
      }
      if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
        return;
      }
      const row = filtered[cursor];
      if (row === undefined) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        openRow(row.id);
        return;
      }
      if (e.key === 'x') {
        e.preventDefault();
        toggleCheck(row.id);
        return;
      }
      if (e.key === 'f') {
        e.preventDefault();
        flagRow(row);
        return;
      }
      if (e.key === 'e') {
        e.preventDefault();
        exportRows([row]);
        return;
      }
      if (e.key === 'a') {
        e.preventDefault();
        archiveRows([row]);
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    filtered,
    cursor,
    openId,
    showShortcuts,
    openRow,
    closeDetail,
    toggleCheck,
    flagRow,
    exportRows,
    archiveRows,
  ]);

  // Move focus into the detail panel when it opens.
  useEffect(() => {
    if (openId !== null) detailRef.current?.focus();
  }, [openId]);

  // Keep the cursor row visible.
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-rowidx="${cursor}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const allVisibleChecked = filtered.length > 0 && filtered.every((r) => checked.has(r.id));
  const flaggedCount = counts['flagged'] ?? 0;

  return (
    <div className="rsp-page">
      <PageHeader
        title="Responses"
        subtitle={`${counts['all'] ?? 0} total · ${counts['needs-review'] ?? 0} need review · ${flaggedCount} flagged`}
        breadcrumb={[{ label: 'Home', href: '/dashboard' }, { label: 'Responses' }]}
        action={
          <button
            type="button"
            className="rsp-iconbtn"
            aria-label="Keyboard shortcuts"
            onClick={() => setShowShortcuts(true)}
          >
            <Keyboard size={18} strokeWidth={1.5} aria-hidden="true" />
          </button>
        }
      />

      {/* Toolbar: search + status filter chips */}
      <div className="rsp-toolbar">
        <div className="rsp-searchbox">
          <Search size={16} strokeWidth={1.6} aria-hidden="true" className="rsp-searchbox-icon" />
          <input
            ref={searchRef}
            data-ui-input=""
            type="search"
            placeholder="Search name, form, or ref…  ( / )"
            aria-label="Search responses"
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
          />
        </div>
        <div className="rsp-filters" role="group" aria-label="Filter by status">
          <button
            type="button"
            className={'rsp-chip' + (statusFilter === 'all' ? ' is-on' : '')}
            aria-pressed={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          >
            All <span className="rsp-chip-n">{counts['all'] ?? 0}</span>
          </button>
          {STATUS_ORDER.filter((s) => (counts[s] ?? 0) > 0).map((s) => (
            <button
              key={s}
              type="button"
              className={'rsp-chip' + (statusFilter === s ? ' is-on' : '')}
              aria-pressed={statusFilter === s}
              onClick={() => setStatusFilter(statusFilter === s ? 'all' : s)}
            >
              <span
                className="rsp-chip-dot"
                style={{ background: `rgb(var(${statusMeta(s).dot}))` }}
                aria-hidden="true"
              />
              {statusMeta(s).label} <span className="rsp-chip-n">{counts[s] ?? 0}</span>
            </button>
          ))}
          <button
            type="button"
            className={'rsp-chip' + (statusFilter === 'flagged' ? ' is-on' : '')}
            aria-pressed={statusFilter === 'flagged'}
            onClick={() => setStatusFilter(statusFilter === 'flagged' ? 'all' : 'flagged')}
          >
            <Flag size={13} strokeWidth={1.6} aria-hidden="true" />
            Flagged <span className="rsp-chip-n">{flaggedCount}</span>
          </button>
          <button
            type="button"
            className={'rsp-chip' + (viewArchived ? ' is-on' : '')}
            aria-pressed={viewArchived}
            onClick={() => {
              setViewArchived((v) => !v);
              setChecked(new Set());
              setOpenId(null);
            }}
          >
            <Archive size={13} strokeWidth={1.6} aria-hidden="true" />
            {viewArchived ? 'Viewing archived' : 'Archived'}
          </button>
        </div>
      </div>

      {/* Bulk action bar */}
      {checked.size > 0 ? (
        <div className="rsp-bulkbar" role="region" aria-label="Bulk actions">
          <span className="rsp-bulkbar-count">{checked.size} selected</span>
          <div className="rsp-bulkbar-actions">
            <button data-ui-button="" data-variant="secondary" onClick={bulkExport}>
              <Download size={15} strokeWidth={1.6} aria-hidden="true" /> Export
            </button>
            <button data-ui-button="" data-variant="secondary" onClick={bulkFlag}>
              <Flag size={15} strokeWidth={1.6} aria-hidden="true" /> Flag
            </button>
            <button data-ui-button="" data-variant="secondary" onClick={bulkArchive}>
              {viewArchived ? (
                <>
                  <ArchiveRestore size={15} strokeWidth={1.6} aria-hidden="true" /> Unarchive
                </>
              ) : (
                <>
                  <Archive size={15} strokeWidth={1.6} aria-hidden="true" /> Archive
                </>
              )}
            </button>
          </div>
          <button type="button" className="rsp-linkbtn" onClick={() => setChecked(new Set())}>
            Clear
          </button>
        </div>
      ) : null}

      {/* List + detail split */}
      <div className={'rsp-split' + (openId !== null ? ' is-open' : '')}>
        <div className="rsp-listcard">
          <div className="rsp-list" ref={listRef} role="table" aria-label="Responses">
            <div className="rsp-row rsp-row--head" role="row">
              <span className="rsp-col-check" role="columnheader">
                <label className="rsp-cbx">
                  <input
                    type="checkbox"
                    checked={allVisibleChecked}
                    aria-label="Select all visible"
                    onChange={(e) => {
                      const next = new Set(checked);
                      for (const r of filtered) {
                        if (e.currentTarget.checked) next.add(r.id);
                        else next.delete(r.id);
                      }
                      setChecked(next);
                    }}
                  />
                </label>
              </span>
              <span className="rsp-col-status" role="columnheader">
                Status
              </span>
              <span className="rsp-col-name" role="columnheader">
                Patient / Form
              </span>
              <span className="rsp-col-date" role="columnheader">
                Submitted
              </span>
              <span className="rsp-col-flag" role="columnheader">
                <span className="sr-only">Flag</span>
              </span>
              <span className="rsp-col-act" role="columnheader">
                <span className="sr-only">Actions</span>
              </span>
            </div>

            {responsesQuery.isLoading ? (
              <div className="rsp-skeletons" aria-busy="true" aria-label="Loading responses">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="rsp-skeleton-row" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="rsp-empty">
                <p className="rsp-empty-title">No responses match.</p>
                <p className="muted">Try clearing the search or the status filter.</p>
              </div>
            ) : (
              filtered.map((r, i) => {
                const isCursor = i === cursor;
                const isChecked = checked.has(r.id);
                const isOpen = r.id === openId;
                return (
                  <div
                    key={r.id}
                    data-rowidx={i}
                    data-testid="response-row"
                    role="row"
                    tabIndex={0}
                    className={
                      'rsp-row' +
                      (isCursor ? ' is-cursor' : '') +
                      (isChecked ? ' is-checked' : '') +
                      (isOpen ? ' is-open' : '')
                    }
                    aria-current={isCursor ? 'true' : undefined}
                    onClick={() => {
                      setCursor(i);
                      openRow(r.id);
                    }}
                    onFocus={() => setCursor(i)}
                  >
                    <span
                      className="rsp-col-check"
                      role="cell"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label className="rsp-cbx">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          aria-label={`Select ${r.name}`}
                          onChange={() => toggleCheck(r.id)}
                        />
                      </label>
                    </span>
                    <span className="rsp-col-status" role="cell">
                      <StatusBadge status={r.status} />
                    </span>
                    <span className="rsp-col-name" role="cell">
                      <span className="rsp-name">{r.name}</span>
                      <span className="rsp-form">
                        {r.form} · <span className="rsp-ref">{r.ref}</span>
                      </span>
                    </span>
                    <span className="rsp-col-date" role="cell">
                      {formatWhen(r.submittedAt)}
                    </span>
                    <span className="rsp-col-flag" role="cell">
                      {r.flagged ? (
                        <span className="rsp-flagmark" title="Flagged for review">
                          <Flag
                            size={15}
                            strokeWidth={1.6}
                            fill="currentColor"
                            aria-hidden="true"
                          />
                          <span className="sr-only">Flagged</span>
                        </span>
                      ) : (
                        <span className="sr-only">Not flagged</span>
                      )}
                    </span>
                    <span className="rsp-col-act" role="cell" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenu.Trigger asChild>
                          <button
                            type="button"
                            className="rsp-iconbtn rsp-iconbtn--sm"
                            aria-label={`Actions for ${r.name}`}
                          >
                            <MoreVertical size={18} strokeWidth={1.6} aria-hidden="true" />
                          </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content>
                          <DropdownMenu.Item onSelect={() => openRow(r.id)}>Open</DropdownMenu.Item>
                          <DropdownMenu.Item onSelect={() => flagRow(r)}>
                            {r.flagged ? 'Remove flag' : 'Flag for review'}
                          </DropdownMenu.Item>
                          <DropdownMenu.Item onSelect={() => exportRows([r])}>
                            Export (CSV)
                          </DropdownMenu.Item>
                          <DropdownMenu.Item onSelect={() => archiveRows([r])}>
                            {viewArchived ? 'Unarchive' : 'Archive'}
                          </DropdownMenu.Item>
                          <DropdownMenu.Separator />
                          <DropdownMenu.Item onSelect={() => navigate(`/responses/${r.id}`)}>
                            Open full record
                          </DropdownMenu.Item>
                        </DropdownMenu.Content>
                      </DropdownMenu>
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {openId !== null ? (
          <DetailPanel
            key={openId}
            id={openId}
            panelRef={detailRef}
            onClose={closeDetail}
            onFlag={(row) => flagRow(row)}
            onExport={(row) => exportRows([row])}
            onMarkReviewed={markReviewed}
            onOpenFull={() => navigate(`/responses/${openId}`)}
            formTitle={titleById}
          />
        ) : null}
      </div>

      <p className="rsp-hint muted">
        Press <kbd>?</kbd> for shortcuts · <kbd>j</kbd>/<kbd>k</kbd> to move · <kbd>Enter</kbd> to
        open
      </p>

      <Dialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <Dialog.Content width="26rem">
          <Dialog.Title>Keyboard shortcuts</Dialog.Title>
          <div className="rsp-cheatgrid">
            {SHORTCUTS.map(([k, d]) => (
              <div className="rsp-cheatrow" key={k}>
                <kbd>{k}</kbd>
                <span>{d}</span>
              </div>
            ))}
          </div>
        </Dialog.Content>
      </Dialog>
    </div>
  );
}

/* ---------------- detail panel ---------------- */

interface DetailPanelProps {
  id: string;
  panelRef: React.RefObject<HTMLElement>;
  onClose: () => void;
  onFlag: (row: RowVM) => void;
  onExport: (row: RowVM) => void;
  onMarkReviewed: (id: string, name: string) => void;
  onOpenFull: () => void;
  formTitle: Map<string, string>;
}

function DetailPanel(props: DetailPanelProps) {
  const { id, panelRef, onClose, onFlag, onExport, onMarkReviewed, onOpenFull, formTitle } = props;
  const detail = useResponse(id);

  const data = detail.data;
  const name =
    data?.patient?.displayName ??
    data?.patient?.email ??
    (data ? `Response ${id.slice(0, 8)}` : '…');
  const form = data ? (formTitle.get(data.questionnaireId) ?? 'Intake form') : '';
  const answers = useMemo(() => (data ? extractAnswers(data) : []), [data]);
  const clinicalFindings = (data?.findings ?? []).filter((f) => f.linkId === undefined);

  const rowVM: RowVM | null = data
    ? {
        id,
        name,
        form,
        status: data.status,
        flagged: data.flagged,
        submittedAt: data.submittedAt,
        ref: id.slice(0, 8),
      }
    : null;

  return (
    <aside
      className="rsp-detail"
      ref={panelRef}
      tabIndex={-1}
      aria-label={`Response detail — ${name}`}
    >
      <div className="rsp-detail-bar">
        <nav className="rsp-crumbs" aria-label="Breadcrumb">
          <button type="button" className="rsp-linkbtn" onClick={onClose}>
            Responses
          </button>
          <ChevronRight size={13} strokeWidth={1.6} aria-hidden="true" />
          <span aria-current="page" className="rsp-ref">
            {id.slice(0, 8)}
          </span>
        </nav>
        <button
          type="button"
          className="rsp-iconbtn"
          aria-label="Close detail (Esc)"
          onClick={onClose}
        >
          <X size={18} strokeWidth={1.7} aria-hidden="true" />
        </button>
      </div>

      {detail.isLoading ? (
        <div className="rsp-detail-body">
          <div className="rsp-skeleton-row" />
          <div className="rsp-skeleton-row" />
          <div className="rsp-skeleton-row" />
        </div>
      ) : data === undefined || rowVM === null ? (
        <div className="rsp-detail-body">
          <p className="muted">Could not load this response.</p>
        </div>
      ) : (
        <div className="rsp-detail-body">
          <div className="rsp-detail-head">
            <div>
              <h2 className="rsp-detail-name">{name}</h2>
              <p className="rsp-detail-meta">
                {form} · received {formatWhen(data.submittedAt)}
              </p>
            </div>
            <StatusBadge status={data.status} />
          </div>

          <div className="rsp-detail-actions">
            <button data-ui-button="" data-variant="secondary" onClick={() => onFlag(rowVM)}>
              <Flag
                size={15}
                strokeWidth={1.6}
                fill={data.flagged ? 'currentColor' : 'none'}
                aria-hidden="true"
              />
              {data.flagged ? 'Flagged' : 'Flag'}
            </button>
            <button data-ui-button="" data-variant="secondary" onClick={() => onExport(rowVM)}>
              <Download size={15} strokeWidth={1.6} aria-hidden="true" /> Export
            </button>
            <button
              data-ui-button=""
              data-variant="primary"
              onClick={() => onMarkReviewed(id, name)}
              disabled={data.status === 'verified'}
            >
              {data.status === 'verified' ? 'Reviewed' : 'Mark reviewed'}
            </button>
          </div>

          {clinicalFindings.length > 0 ? (
            <div className="rsp-detail-findings" role="status">
              {clinicalFindings.map((f, i) => (
                <p key={`${f.code}-${String(i)}`}>{f.message}</p>
              ))}
            </div>
          ) : null}

          <h3 className="rsp-detail-sec">Submitted answers</h3>
          {answers.length === 0 ? (
            <p className="muted">No answers recorded.</p>
          ) : (
            <dl className="rsp-answers">
              {answers.map((a, i) => (
                <div className="rsp-answer" key={`${a.label}-${String(i)}`}>
                  <dt>{a.label}</dt>
                  <dd>{a.value}</dd>
                </div>
              ))}
            </dl>
          )}

          <button type="button" className="rsp-linkbtn rsp-detail-full" onClick={onOpenFull}>
            Open full record (notes & history)
            <ChevronRight size={14} strokeWidth={1.6} aria-hidden="true" />
          </button>
        </div>
      )}
    </aside>
  );
}

/* ---------------- answer extraction ---------------- */

interface AnswerPair {
  label: string;
  value: string;
}

function extractAnswers(detail: ResponseDetail): AnswerPair[] {
  const out: AnswerPair[] = [];
  const itemTexts = detail.itemTexts;
  const resource = detail.resource as { item?: unknown };
  const walk = (items: unknown): void => {
    if (!Array.isArray(items)) return;
    for (const raw of items) {
      if (raw === null || typeof raw !== 'object') continue;
      const item = raw as { linkId?: unknown; answer?: unknown; item?: unknown };
      const linkId = typeof item.linkId === 'string' ? item.linkId : undefined;
      if (Array.isArray(item.answer) && item.answer.length > 0 && linkId !== undefined) {
        const value = item.answer
          .map(formatAnswer)
          .filter((s) => s !== '')
          .join(', ');
        if (value !== '') out.push({ label: itemTexts[linkId] ?? linkId, value });
      }
      if (Array.isArray(item.item)) walk(item.item);
    }
  };
  walk(resource.item);
  return out;
}

function formatAnswer(raw: unknown): string {
  if (raw === null || typeof raw !== 'object') return '';
  const a = raw as Record<string, unknown>;
  if (typeof a['valueString'] === 'string') return a['valueString'];
  if (typeof a['valueBoolean'] === 'boolean') return a['valueBoolean'] ? 'Yes' : 'No';
  if (typeof a['valueDate'] === 'string') return a['valueDate'];
  if (typeof a['valueInteger'] === 'number') return String(a['valueInteger']);
  if (typeof a['valueDecimal'] === 'number') return String(a['valueDecimal']);
  const coding = a['valueCoding'];
  if (coding !== null && typeof coding === 'object') {
    const c = coding as Record<string, unknown>;
    if (typeof c['display'] === 'string') return c['display'];
    if (typeof c['code'] === 'string') return c['code'];
  }
  const attachment = a['valueAttachment'];
  if (attachment !== null && typeof attachment === 'object') {
    const at = attachment as Record<string, unknown>;
    return typeof at['title'] === 'string' && at['title'] !== '' ? at['title'] : 'File attached';
  }
  return '';
}
